import { Box, Text } from '@mantine/core'
import { DEFAULT_CRITERIA } from '../constants'
import { ScorePicker } from './ScorePicker'

interface TeacherAssessmentInputProps {
  value: number
  onChange: (score: number) => void
  disabled?: boolean
  color?: 'green' | 'blue'
  size?: number
  /** Per-item rubric text keyed by score ("1".."5") — shown on hover over the
   *  score buttons and preferred over DEFAULT_CRITERIA below them (AR-828). */
  rubrics?: Record<string, string>
}

/** Two-line criteria slot reservation (prevents card height jumps). */
const CRITERIA_MIN_HEIGHT = 44

/**
 * The 5 score buttons + the criteria text that appears once a score is picked.
 *
 * The label is rendered by the parent (`StudentAssessmentRow`) because it
 * shares a header line with the per-student "Enable assessment" switch.
 *
 * The wrapper occupies 100% of the parent's column (a fixed 318 px in
 * desktop) so the picker hugs the right edge of the card and the criteria
 * text wraps inside the same width. The criteria slot is always present in
 * the DOM with a reserved min-height to prevent vertical jumps when the
 * score is selected/cleared or the row is enabled/disabled.
 */
export function TeacherAssessmentInput({
  value,
  onChange,
  disabled,
  color = 'green',
  size = 56,
  rubrics,
}: TeacherAssessmentInputProps) {
  const selected = value >= 1 && value <= 5 ? (value as 1 | 2 | 3 | 4 | 5) : null
  const criteriaText = selected ? rubrics?.[String(selected)] || DEFAULT_CRITERIA[selected] : ''
  const showCriteria = selected !== null && !disabled

  return (
    <Box
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 6,
        width: '100%',
      }}
    >
      <ScorePicker
        value={selected ?? undefined}
        onChange={onChange}
        disabled={disabled}
        size={size}
        position="left"
        color={color}
        rubrics={rubrics}
      />
      <Box
        data-testid="criteria-slot"
        style={{ minHeight: CRITERIA_MIN_HEIGHT, width: '100%' }}
      >
        {showCriteria && (
          <Text size="sm" italic style={{ wordBreak: 'break-word' }}>
            {selected} — {criteriaText}
          </Text>
        )}
      </Box>
    </Box>
  )
}
